import { Component, ElementRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { forkJoin } from 'rxjs';

// Template remains identical, replacing class variables/methods below
@Component({
  selector: 'app-admin-dispatch-unassigned',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <!-- Template remains mostly identical with minor fixes below for variable names if needed, but best keeping structure -->
    <div class="min-h-full relative overflow-y-auto admin-premium-bg">
      @if (!selectedJob()) {
        <div class="max-w-[1700px] mx-auto p-5">
        <div class="flex items-center justify-between mb-5 shrink-0">
          <div>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">Unassigned Jobs</h1>
            <p class="text-slate-500 text-xs">Jobs waiting to be allocated to a plumber.</p>
          </div>
          <button (click)="openCreateModal()" class="px-3 py-2 text-white rounded-lg text-xs font-semibold shadow-sm transition-opacity hover:opacity-90 cursor-pointer flex items-center gap-2" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);"><i class="ti ti-plus text-[16px]"></i> Create Job</button>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-4">
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
            <div><p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Waiting</p><p class="text-lg font-semibold text-slate-900 mt-0.5">{{ jobs().length }}</p></div>
            <span class="w-8 h-8 rounded-lg bg-rose-50 border border-rose-100 text-rose-600 flex items-center justify-center"><i class="ti ti-inbox"></i></span>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
            <div><p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Available Team</p><p class="text-lg font-semibold text-[#1A6FA6] mt-0.5">{{ workers().length }}</p></div>
            <span class="w-8 h-8 rounded-lg bg-[#EAF5FA] border border-[#D5EBF6] text-[#1A6FA6] flex items-center justify-center"><i class="ti ti-users"></i></span>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
            <div><p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Inspection Links</p><p class="text-lg font-semibold text-slate-900 mt-0.5">{{ inspectionJobs().length }}</p></div>
            <span class="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 text-slate-500 flex items-center justify-center"><i class="ti ti-clipboard-search"></i></span>
          </div>
        </div>
        <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div class="inline-flex rounded-lg border border-[#E7EEF5] bg-white p-1 shadow-sm">
            <button type="button" (click)="jobFilter.set('All')" class="h-8 px-3 rounded-md text-xs font-bold transition-colors" [class]="jobFilter() === 'All' ? 'bg-[#EAF5FA] text-[#1A6FA6]' : 'text-slate-500 hover:text-slate-900'">All</button>
            <button type="button" (click)="jobFilter.set('Urgent')" class="h-8 px-3 rounded-md text-xs font-bold transition-colors" [class]="jobFilter() === 'Urgent' ? 'bg-rose-50 text-rose-700' : 'text-slate-500 hover:text-slate-900'">Urgent</button>
            <button type="button" (click)="jobFilter.set('Routine')" class="h-8 px-3 rounded-md text-xs font-bold transition-colors" [class]="jobFilter() === 'Routine' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-900'">Routine</button>
          </div>
          <p class="text-xs font-semibold text-slate-500">{{ filteredJobs().length }} shown - {{ urgentJobsCount() }} urgent</p>
        </div>
        <div class="jobs-grid">
          @if (isLoading()) {
            @for (n of skeletons; track n) {
              <div class="bg-white rounded-3xl shadow-sm p-6 border border-slate-100 animate-pulse">
                <div class="flex items-center gap-2 mb-4">
                  <div class="h-5 w-20 bg-slate-200 rounded-full"></div>
                  <div class="h-4 w-10 bg-slate-100 rounded-full"></div>
                </div>
                <div class="h-5 w-3/4 bg-slate-200 rounded-xl mb-2"></div>
                <div class="h-4 w-1/2 bg-slate-100 rounded-xl mb-4"></div>
                <div class="bg-slate-100 rounded-2xl p-4 h-16 mb-4"></div>
                <div class="flex justify-end pt-4 border-t border-slate-100">
                  <div class="h-8 w-28 bg-slate-200 rounded-xl"></div>
                </div>
              </div>
            }
          } @else if (jobs().length === 0) {
            <div class="col-span-full bg-white rounded-3xl shadow-sm p-16 flex flex-col items-center justify-center text-center border border-slate-100">
              <div class="w-24 h-24 rounded-full flex items-center justify-center mb-6" style="background:linear-gradient(135deg, rgba(43,154,210,0.1), rgba(94,201,240,0.1));">
                <i class="ti ti-clipboard-list text-5xl" style="color:#2B9AD2;"></i>
              </div>
              <h3 class="text-2xl font-black text-slate-900 mb-2">No Unassigned Jobs</h3>
              <p class="text-slate-500 text-sm max-w-md mx-auto mb-8 leading-relaxed">Your dispatch board is clear! All jobs and projects have been allocated to the team. You can create a new job or project when ready.</p>
              <button (click)="openCreateModal()" class="px-8 py-3.5 text-white rounded-2xl font-bold shadow-sm hover:opacity-90 flex items-center gap-2 cursor-pointer transition-transform hover:scale-105" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
                <i class="ti ti-plus text-lg"></i> Create Job or Project
              </button>
            </div>
          } @else if (filteredJobs().length === 0) {
            <div class="col-span-full bg-white rounded-3xl shadow-sm p-12 flex flex-col items-center justify-center text-center border border-slate-100">
              <div class="w-16 h-16 rounded-2xl bg-slate-50 border border-slate-100 text-slate-400 flex items-center justify-center mb-4">
                <i class="ti ti-filter-off text-3xl"></i>
              </div>
              <h3 class="text-xl font-black text-slate-900 mb-2">No {{ jobFilter().toLowerCase() }} jobs</h3>
              <p class="text-slate-500 text-sm max-w-md mx-auto mb-6">There are no unassigned jobs in this filter.</p>
              <button type="button" (click)="jobFilter.set('All')" class="h-10 px-5 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-700 shadow-sm hover:bg-slate-50">Show all</button>
            </div>
          } @else {
            @for (job of filteredJobs(); track job.id) {
              <div class="job-card">
                  <div class="card-header">
                      <div class="flex flex-wrap items-center gap-2">
                        <span class="job-status status-unassigned">
                            UNASSIGNED
                        </span>
                        @if (isUrgent(job)) {
                          <span class="job-status status-error">
                              URGENT
                          </span>
                        }
                      </div>
                      <span class="text-xs font-bold text-slate-400">#{{job.id}}</span>
                  </div>

                  <div class="card-body">
                      <h3>{{job.customer}}</h3>
                      <p class="job-date"><i class="ti ti-map-pin"></i> {{job.address}}</p>
                      
                      <div class="bg-slate-50 p-3 rounded-lg border border-slate-100 mt-2">
                          <p class="text-xs text-slate-600 line-clamp-3">{{job.desc}}</p>
                      </div>
                  </div>

                  <div class="card-footer">
                      <div class="flex gap-2">
                        <a [routerLink]="['/admin/dispatch/job', job.id]" class="btn-open">Open</a>
                        <button class="btn-open" (click)="selectJob(job)">Assign</button>
                      </div>
                  </div>
              </div>
            }
          }
        </div>
        </div>
      } @else {
        @if (selectedJob(); as job) {
          <div class="max-w-4xl mx-auto space-y-5">
            <button (click)="selectJob(null)" class="flex items-center gap-1.5 text-slate-600 hover:text-slate-900 font-bold text-sm mb-2"><i class="ti ti-arrow-left"></i> Back to Unassigned List</button>
            <div class="bg-white rounded-3xl shadow-sm p-6 flex justify-between items-start border border-slate-100">
              <div><span class="text-[11px] font-bold text-slate-400 uppercase">#{{job.id}}</span><h2 class="text-2xl font-black text-slate-900 mt-1">{{job.customer}}</h2><p class="text-sm text-slate-500 mt-1.5"><i class="ti ti-map-pin"></i> {{job.address}}</p></div>
            </div>
             <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div class="md:col-span-2 bg-white rounded-3xl shadow-sm p-6 border border-slate-100 flex flex-col">
                   <h3 class="text-sm font-bold text-slate-900 mb-4 flex justify-between items-center">
                     Select Plumbers
                     <span class="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{{ selectedWorkerIds().length }} Selected</span>
                   </h3>
                   
                   <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 overflow-y-auto max-h-[300px] pr-2 custom-scrollbar">
                     @for (worker of workers(); track worker.id) {
                       <button (click)="toggleWorker(worker.id)" 
                         class="p-3 rounded-2xl border-2 transition-all flex flex-col items-center text-center gap-2 group relative"
                         [class]="isWorkerSelected(worker.id) ? 'border-[#2B9AD2] bg-blue-50/50' : 'border-slate-50 hover:border-slate-200 bg-slate-50/30'">
                         
                         @if (isWorkerSelected(worker.id)) {
                           <div class="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#2B9AD2] text-white flex items-center justify-center shadow-sm">
                             <i class="ti ti-check text-[12px]"></i>
                           </div>
                         }

                         <div class="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shadow-sm"
                           [class]="isWorkerSelected(worker.id) ? 'bg-[#2B9AD2] text-white' : 'bg-white text-slate-400 border border-slate-100'">
                           {{ worker.name.charAt(0) }}
                         </div>
                         <div class="flex-1">
                           <p class="text-xs font-bold leading-tight" [class.text-[#2B9AD2]]="isWorkerSelected(worker.id)">{{ worker.name }}</p>
                           <p class="text-[9px] font-bold text-slate-400 mt-0.5 uppercase tracking-tighter">{{ worker.role === 'Worker' ? 'Plumber' : worker.role }}</p>
                         </div>
                       </button>
                     }
                   </div>
                </div>

                <div class="bg-white rounded-3xl shadow-sm p-6 border border-slate-100 flex flex-col">
                   <div class="flex-1">
                     <h3 class="text-sm font-bold text-slate-900 mb-2">Assignment Summary</h3>
                     <p class="text-xs text-slate-500 mb-4 leading-relaxed">Assigning multiple technicians allows for collaborative execution on complex tasks.</p>
                     
                     <div class="space-y-2 mb-6">
                        <div class="grid grid-cols-2 gap-2 rounded-2xl border border-slate-100 bg-slate-50/70 p-3">
                          <label class="block">
                            <span class="text-[9px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Scheduled date</span>
                            <input type="date" [(ngModel)]="assignmentDate" class="w-full h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 outline-none focus:border-[#2B9AD2]">
                          </label>
                          <label class="block">
                            <span class="text-[9px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Time</span>
                            <input type="time" [(ngModel)]="assignmentTime" class="w-full h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 outline-none focus:border-[#2B9AD2]">
                          </label>
                          <p class="col-span-2 text-[10px] font-semibold text-slate-400">Required before dispatch so workers receive a scheduled attendance time.</p>
                          @if (scheduleChangeNeedsReason(selectedJob(), assignmentDate && assignmentTime ? assignmentDate + ' ' + assignmentTime + ':00' : null)) {
                            <div class="col-span-2 rounded-xl border border-amber-100 bg-amber-50 p-3 space-y-2">
                              <p class="text-xs font-bold text-amber-800">Reschedule reason</p>
                              <select [(ngModel)]="scheduleChangeCategory" class="w-full h-9 rounded-lg border border-amber-100 bg-white px-2 text-xs font-semibold text-slate-700 outline-none">
                                @for (category of scheduleChangeCategories; track category) {
                                  <option [value]="category">{{ category }}</option>
                                }
                              </select>
                              <textarea [(ngModel)]="scheduleChangeRemarks" rows="3" placeholder="Why is the schedule changing?"
                                        class="w-full rounded-lg border border-amber-100 bg-white px-3 py-2 text-xs outline-none resize-none"></textarea>
                            </div>
                          }
                        </div>
                        @if (selectedWorkerIds().length === 0) {
                          <div class="p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                            <i class="ti ti-users text-slate-300 text-2xl mb-1 block"></i>
                            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-normal">No Plumbers Selected</p>
                          </div>
                        } @else {
                          @for (id of selectedWorkerIds(); track id) {
                            <div class="p-2 bg-blue-50 border border-blue-100 rounded-xl">
                              <div class="flex items-center justify-between gap-2">
                                <div class="flex items-center gap-2 min-w-0">
                                  <div class="w-6 h-6 rounded-lg bg-[#2B9AD2] text-white flex items-center justify-center text-[10px] font-bold shrink-0">{{ getWorkerName(id).charAt(0) }}</div>
                                  <span class="text-xs font-bold text-slate-700 truncate">{{ getWorkerName(id) }}</span>
                                </div>
                                <button (click)="toggleWorker(id)" class="text-slate-400 hover:text-red-500 transition-colors p-1"><i class="ti ti-circle-x"></i></button>
                              </div>
                              <label class="block mt-2">
                                <span class="text-[9px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Fleet optional</span>
                                <select [(ngModel)]="selectedFleetVehicleIds[id]" class="w-full h-9 rounded-lg border border-blue-100 bg-white px-2 text-xs font-semibold text-slate-700 outline-none focus:border-[#2B9AD2]">
                                  <option value="">No vehicle</option>
                                  @for (vehicle of fleetVehicles(); track vehicle.id) {
                                    <option [value]="vehicle.id">{{ fleetVehicleLabel(vehicle) }}</option>
                                  }
                                </select>
                              </label>
                            </div>
                          }
                          @if (fleetVehicles().length === 0) {
                            <div class="rounded-xl border border-slate-100 bg-slate-50 p-3 text-[10px] font-semibold text-slate-400">
                              No active fleet vehicles available.
                            </div>
                          }
                        }
                     </div>
                   </div>

                   <button (click)="allocateJob(job)" [disabled]="selectedWorkerIds().length === 0 || !assignmentDate || !assignmentTime || isAllocating() || scheduleReasonInvalid(selectedJob(), assignmentDate + ' ' + assignmentTime + ':00')" class="w-full h-14 text-white rounded-2xl font-bold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-[0_8px_20px_rgba(43,154,210,0.25)]" style="background: linear-gradient(135deg, #2B9AD2, #1A6FA6);">
                      @if (isAllocating()) {
                         <i class="ti ti-loader animate-spin text-[20px]"></i> Dispatching...
                      } @else {
                         <i class="ti ti-rocket text-[20px]"></i> Dispatch to {{ selectedWorkerIds().length }} {{ selectedWorkerIds().length === 1 ? 'Worker' : 'Workers' }}
                      }
                   </button>
                </div>
             </div>
          </div>
        }
      }
      <!-- Create Job/Project Modal -->
      @if (showCreateModal()) {
        <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div class="bg-white rounded-3xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-100">
            <div class="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50/50">
              <h2 class="text-xl font-bold text-slate-900">{{ createMode() === 'Project' ? 'Create New Project' : 'Create New Job' }}</h2>
              <button (click)="closeCreateModal()" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-500 transition-colors cursor-pointer"><i class="ti ti-x text-lg"></i></button>
            </div>
            <form #jobForm="ngForm" (ngSubmit)="onSubmit(jobForm)" class="flex flex-col flex-1 overflow-hidden">
              <div class="p-6 overflow-y-auto flex-1 space-y-6">
                <div class="space-y-4">
                  <h3 class="text-sm font-bold text-slate-900 uppercase">Customer Details</h3>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="col-span-1 md:col-span-2 relative">
                      <label class="block text-sm font-semibold text-slate-700 mb-1">Phone Number <span class="text-xs text-slate-400 font-normal ml-1">(Search to autofill)</span></label>
                      <div class="flex items-center h-12 rounded-xl border-2 transition-all bg-[#FAFBFC] focus-within:border-[#2B9AD2] focus-within:bg-white" style="border-color:#E2E8F0;">
                        <div class="px-4 text-slate-500 font-bold border-r-2 border-slate-100 bg-slate-50/50 rounded-l-xl h-full flex items-center">+64</div>
                        <input type="text" name="phone" [(ngModel)]="searchPhone" (ngModelChange)="onPhoneChange($event)" class="w-full h-full px-4 outline-none font-medium text-slate-700 bg-transparent" placeholder="e.g. 21 123 4567" autocomplete="off">
                      </div>
                      
                      @if (searchResults().length > 0) {
                        <div class="absolute w-full mt-2 bg-white rounded-xl shadow-lg border border-slate-100 z-10 overflow-hidden">
                          @for (customer of searchResults(); track customer.phone) {
                            <div (click)="selectCustomer(customer)" class="p-3 hover:bg-slate-50 border-b border-slate-50 last:border-0 cursor-pointer transition-colors">
                              <p class="text-sm font-bold text-slate-800">{{customer.name}}</p>
                              <p class="text-xs text-slate-500 flex items-center gap-2 mt-0.5"><i class="ti ti-phone"></i> {{customer.phone}} <span *ngIf="customer.email" class="ml-2"><i class="ti ti-mail"></i> {{customer.email}}</span></p>
                            </div>
                          }
                        </div>
                      }
                    </div>
                    <div><label class="block text-sm font-semibold text-slate-700 mb-1">Customer Name</label><input type="text" name="customer" [(ngModel)]="customerName" required class="w-full h-12 px-4 rounded-xl border-2 outline-none transition-all font-medium text-slate-700" style="border-color:#E2E8F0; background:#FAFBFC;"></div>
                    <div><label class="block text-sm font-semibold text-slate-700 mb-1">Email {{ createMode() === 'Project' ? '(Required for project)' : '(Optional)' }}</label><input type="email" name="email" [(ngModel)]="customerEmail" [required]="createMode() === 'Project'" class="w-full h-12 px-4 rounded-xl border-2 outline-none transition-all font-medium text-slate-700" style="border-color:#E2E8F0; background:#FAFBFC;"></div>
                    <div class="col-span-1 md:col-span-2">
                      <label class="block text-sm font-semibold text-slate-700 mb-1">Site Address</label>
                      <input #siteAddressInput type="text" name="address" [(ngModel)]="customerAddress" (ngModelChange)="onAddressManualChange()" required class="w-full h-12 px-4 rounded-xl border-2 outline-none transition-all font-medium text-slate-700" style="border-color:#E2E8F0; background:#FAFBFC;">
                      @if (mapsKey()) {
                        <p class="text-[10px] font-semibold text-slate-400 mt-1">Select an Auckland Google Maps address when available, or type the site address manually.</p>
                      }
                    </div>
                  </div>
                </div>
                @if (createMode() === 'Job') {
                <div class="space-y-4 pt-4 border-t">
                  <div>
                    <span class="block text-sm font-semibold text-slate-700 mb-2">Job Category</span>
                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                       <button type="button" class="p-4 border-2 rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer transition-all" [class]="newJobCategory() === 'Inspection' ? 'border-[#2B9AD2] bg-blue-50/50' : 'border-slate-100 hover:border-slate-200'" (click)="newJobCategory.set('Inspection'); emergencyDoubleRate = false">
                         <div class="w-10 h-10 rounded-full flex items-center justify-center" [class]="newJobCategory() === 'Inspection' ? 'bg-[#2B9AD2] text-white' : 'bg-slate-100 text-slate-500'"><i class="ti ti-clipboard-search"></i></div>
                         <span class="font-bold text-sm text-center" [class]="newJobCategory() === 'Inspection' ? 'text-[#2B9AD2]' : 'text-slate-600'">Inspection Quote</span>
                       </button>
                       <button type="button" class="p-4 border-2 rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer transition-all" [class]="newJobCategory() === 'Service' ? 'border-[#2B9AD2] bg-blue-50/50' : 'border-slate-100 hover:border-slate-200'" (click)="newJobCategory.set('Service'); emergencyDoubleRate = false">
                         <div class="w-10 h-10 rounded-full flex items-center justify-center" [class]="newJobCategory() === 'Service' ? 'bg-[#2B9AD2] text-white' : 'bg-slate-100 text-slate-500'"><i class="ti ti-tool"></i></div>
                         <span class="font-bold text-sm" [class]="newJobCategory() === 'Service' ? 'text-[#2B9AD2]' : 'text-slate-600'">Service Job</span>
                       </button>
                       <button type="button" class="p-4 border-2 rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer transition-all" [class]="newJobCategory() === 'Emergency' ? 'border-red-300 bg-red-50' : 'border-slate-100 hover:border-slate-200'" (click)="newJobCategory.set('Emergency')">
                         <div class="w-10 h-10 rounded-full flex items-center justify-center" [class]="newJobCategory() === 'Emergency' ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-500'"><i class="ti ti-alert-triangle"></i></div>
                         <span class="font-bold text-sm" [class]="newJobCategory() === 'Emergency' ? 'text-red-700' : 'text-slate-600'">Emergency</span>
                       </button>
                    </div>
                  </div>
                  @if (newJobCategory() === 'Service') {
                    <div>
                      <label class="block text-sm font-semibold text-slate-700 mb-1">Link Inspection Quote <span class="text-xs text-slate-400 font-normal">(Optional)</span></label>
                      <select name="linked_inspection_id" [(ngModel)]="linkedInspectionId" class="w-full h-12 px-4 rounded-xl border-2 outline-none transition-all cursor-pointer font-medium text-slate-700" style="border-color:#E2E8F0; background:#FAFBFC;">
                        <option value="">No linked inspection</option>
                        @for (inspection of inspectionJobs(); track inspection.id) {
                          <option [value]="inspection.id">{{ inspection.id }} - {{ inspection.customer_name }} - {{ inspection.address }}</option>
                        }
                      </select>
                    </div>
                  }
                  <div>
                    <span class="block text-sm font-semibold text-slate-700 mb-2">Labour Pricing</span>
                    <div class="grid grid-cols-2 gap-3">
                      <button type="button" (click)="pricingType.set('Hourly')" class="h-11 rounded-xl border-2 text-sm font-bold"
                              [class.border-[#2B9AD2]]="pricingType() === 'Hourly'" [class.text-[#1A6FA6]]="pricingType() === 'Hourly'" [class.bg-blue-50]="pricingType() === 'Hourly'"
                              [class.border-slate-100]="pricingType() !== 'Hourly'" [class.text-slate-600]="pricingType() !== 'Hourly'">Hourly</button>
                      <button type="button" (click)="pricingType.set('Fixed')" class="h-11 rounded-xl border-2 text-sm font-bold"
                              [class.border-[#2B9AD2]]="pricingType() === 'Fixed'" [class.text-[#1A6FA6]]="pricingType() === 'Fixed'" [class.bg-blue-50]="pricingType() === 'Fixed'"
                              [class.border-slate-100]="pricingType() !== 'Fixed'" [class.text-slate-600]="pricingType() !== 'Fixed'">Fixed Rate</button>
                    </div>
                    @if (pricingType() === 'Fixed') {
                      <label class="block mt-3">
                        <span class="block text-sm font-semibold text-slate-700 mb-1">Fixed Labour Charge</span>
                        <input type="number" min="0" step="0.01" name="fixedLabourCharge" [(ngModel)]="fixedLabourCharge"
                               class="w-full h-12 px-4 rounded-xl border-2 outline-none transition-all font-medium text-slate-700" style="border-color:#E2E8F0; background:#FAFBFC;">
                      </label>
                    }
                  </div>
                  @if (newJobCategory() === 'Emergency') {
                    <label class="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" name="emergencyDoubleRate" [(ngModel)]="emergencyDoubleRate" class="mt-0.5 rounded border-rose-300 text-rose-600">
                      <span>
                        <span class="block text-sm font-bold text-rose-800">Double emergency labour rate</span>
                        <span class="block text-xs text-rose-600 mt-0.5">Charge 2× the hourly rates or 2× the fixed labour amount. Materials are not doubled.</span>
                      </span>
                    </label>
                  }
                </div>
                }
                <div class="space-y-4 pt-4 border-t">
                  <div><label class="block text-sm font-semibold text-slate-700 mb-1">Description</label><textarea name="desc" ngModel required rows="4" class="w-full p-4 rounded-xl border-2 outline-none transition-all font-medium text-slate-700 resize-none" style="border-color:#E2E8F0; background:#FAFBFC;"></textarea></div>
                </div>
              </div>
              <div class="p-6 border-t border-slate-100 bg-slate-50 flex flex-wrap justify-between gap-3">
                <button type="button" (click)="createMode() === 'Job' ? openProjectModal() : openCreateModal()" class="px-6 py-2.5 text-[#1A6FA6] font-bold rounded-xl hover:bg-blue-50 transition-colors cursor-pointer">
                  {{ createMode() === 'Job' ? 'Create a Project' : 'Create a Job Instead' }}
                </button>
                <div class="flex justify-end gap-3">
                <button type="button" (click)="closeCreateModal()" class="px-6 py-2.5 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors cursor-pointer">Cancel</button>
                <button type="submit" [disabled]="!jobForm.valid" class="px-8 py-2.5 text-white font-bold rounded-xl shadow-sm hover:opacity-90 disabled:opacity-50 transition-all cursor-pointer" style="background:linear-gradient(135deg, #2B9AD2, #1A6FA6);">Create {{ createMode() === 'Project' ? 'Project' : 'Job' }}</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      }
    </div>
  `
})
export class AdminDispatchUnassignedComponent implements OnInit {
  @ViewChild('siteAddressInput') siteAddressInput?: ElementRef<HTMLInputElement>;

  jobs = signal<any[]>([]);
  jobFilter = signal<'All' | 'Urgent' | 'Routine'>('All');
  isLoading = signal(true);
  skeletons = [1, 2, 3, 4, 5, 6];
  showCreateModal = signal(false);
  createMode = signal<'Job' | 'Project'>('Job');
  newJobCategory = signal<'Inspection' | 'Service' | 'Emergency'>('Service');
  selectedJob = signal<any | null>(null);

  searchPhone = '';
  selectedCustomerId: number | null = null;
  customerName = '';
  customerEmail = '';
  customerAddress = '';
  addressPlaceId = '';
  addressLatitude: number | null = null;
  addressLongitude: number | null = null;
  mapsKey = signal('');
  mapsLoaded = signal(false);
  searchResults = signal<any[]>([]);
  inspectionJobs = signal<any[]>([]);
  linkedInspectionId = '';
  pricingType = signal<'Hourly' | 'Fixed'>('Hourly');
  fixedLabourCharge: number | null = null;
  emergencyDoubleRate = false;
  searchTimeout: any;

  workers = signal<any[]>([]);
  fleetVehicles = signal<any[]>([]);
  selectedWorkerIds = signal<number[]>([]);
  selectedFleetVehicleIds: Record<number, string> = {};
  isAllocating = signal<boolean>(false);
  assignmentDate = '';
  assignmentTime = '';
  scheduleChangeCategories = ['Customer Unavailable', 'Parts Not Ready', 'Worker Unavailable', 'Emergency Priority Changed', 'Weather/Site Access', 'Other'];
  scheduleChangeCategory = 'Customer Unavailable';
  scheduleChangeRemarks = '';

  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  ngOnInit() {
    this.loadJobs();
    this.loadWorkers();
    this.loadFleetVehicles();
    this.loadInspectionJobs();
    this.loadMapSettings();
    this.route.queryParamMap.subscribe(params => {
      const create = params.get('create');
      if (create === 'job') {
        this.openCreateModal(false);
      } else if (create === 'project') {
        this.openProjectModal(false);
      }
    });
  }

  toggleWorker(id: number) {
    this.selectedWorkerIds.update(ids => {
      if (ids.includes(id)) {
        delete this.selectedFleetVehicleIds[id];
        return ids.filter(i => i !== id);
      }
      return [...ids, id];
    });
  }

  isWorkerSelected(id: number): boolean {
    return this.selectedWorkerIds().includes(id);
  }

  getWorkerName(id: number): string {
    return this.workers().find(w => w.id === id)?.name || 'Unknown';
  }

  fleetVehicleLabel(vehicle: any): string {
    const rego = vehicle?.rego ? `${vehicle.rego} - ` : '';
    const current = vehicle?.name && vehicle.name !== 'Unassigned' ? ` (${vehicle.name})` : '';
    return `${rego}${vehicle?.vehicle || 'Vehicle'}${current}`;
  }

  isUrgent(job: any): boolean {
    return job?.priority === 'Urgent';
  }

  urgentJobsCount(): number {
    return this.jobs().filter(job => this.isUrgent(job)).length;
  }

  filteredJobs(): any[] {
    const filter = this.jobFilter();
    if (filter === 'Urgent') return this.jobs().filter(job => this.isUrgent(job));
    if (filter === 'Routine') return this.jobs().filter(job => !this.isUrgent(job));
    return this.jobs();
  }

  loadWorkers() {
    this.api.getWorkers().subscribe({
      next: (res: any) => {
        if (res.success && res.data?.users) {
          this.workers.set(res.data.users.filter((u: any) => u.is_active));
        }
      }
    });
  }

  loadFleetVehicles() {
    this.api.getFleetVehicles().subscribe({
      next: (res: any) => {
        if (res.success && res.data?.vehicles) {
          this.fleetVehicles.set(res.data.vehicles.filter((vehicle: any) => vehicle.is_active !== false));
        }
      }
    });
  }

  loadJobs() {
    this.isLoading.set(true);
    this.api.getJobs('Unassigned').subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success && res.data?.jobs) {
          const uniqueJobsMap = new Map();
          
          res.data.jobs.forEach((j: any) => {
             const jobId = j.job_id || j.id;
             if (!uniqueJobsMap.has(jobId)) {
                 uniqueJobsMap.set(jobId, {
                    id: jobId,
                    type: j.type,
                    priority: j.priority || 'Routine',
                    customer: j.customer_name || 'Anonymous',
                    address: j.address || 'Unknown Address',
                    desc: j.description,
                    plumbers: new Set()
                 });
             }
             const existing = uniqueJobsMap.get(jobId);
             
             // In case the backend returns discrete rows for each assignment instead of GROUP_CONCAT
             if (j.assigned_workers) {
                 j.assigned_workers.split(',').forEach((w: string) => existing.plumbers.add(w.trim()));
             } else if (j.worker_name || j.plumber_name) {
                 existing.plumbers.add(j.worker_name || j.plumber_name);
             }
          });

          this.jobs.set(Array.from(uniqueJobsMap.values()).map(job => {
            return {
              ...job,
              plumber: job.plumbers.size > 0 ? Array.from(job.plumbers).join(', ') : 'Unassigned'
            };
          }));
        }
      },
      error: () => this.isLoading.set(false)
    });
  }

  loadInspectionJobs() {
    this.api.getJobs().subscribe({
      next: (res: any) => {
        if (res.success && res.data?.jobs) {
          this.inspectionJobs.set(
            res.data.jobs.filter((j: any) => j.type === 'Inspection' && ['Quote Sent', 'Approved', 'Completed'].includes(j.status))
          );
        }
      }
    });
  }

  selectJob(job: any) { 
    this.selectedJob.set(job); 
    this.selectedWorkerIds.set([]);
    this.selectedFleetVehicleIds = {};
    this.resetScheduleChangeReason();
    this.setDefaultAssignmentSchedule(job);
  }

  allocateJob(job: any) {
    if (this.selectedWorkerIds().length === 0 || this.isAllocating()) return;
    if (!this.assignmentDate || !this.assignmentTime) {
      showToast('error', 'Select a schedule date and time before dispatching.');
      return;
    }
    const nextScheduledDate = `${this.assignmentDate} ${this.assignmentTime}:00`;
    if (this.scheduleReasonInvalid(job, nextScheduledDate)) {
      showToast('error', 'Add a schedule change reason before dispatching.');
      return;
    }
    this.isAllocating.set(true);

    const payload: any = {
      reference_id: job.id,
      model_type: 'Job', 
      user_ids: this.selectedWorkerIds(),
      scheduled_date: nextScheduledDate
    };
    if (this.scheduleChangeNeedsReason(job, nextScheduledDate)) {
      payload.schedule_change_category = this.scheduleChangeCategory;
      payload.schedule_change_remarks = this.scheduleChangeRemarks.trim();
    }

    this.api.assignWorker(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.saveOptionalFleetAssignments(job, () => {
            this.isAllocating.set(false);
            showToast('success', `Job successfully dispatched to ${this.selectedWorkerIds().length} plumbers!`);
            this.selectedWorkerIds.set([]);
            this.selectedFleetVehicleIds = {};
            this.resetScheduleChangeReason();
            this.selectedJob.set(null);
            this.loadJobs();
            this.loadFleetVehicles();
          });
        } else {
          this.isAllocating.set(false);
          showToast('error', res.message || 'Failed to dispatch job.');
        }
      },
      error: () => {
        this.isAllocating.set(false);
        showToast('error', 'Network error dropping allocation payload.');
      }
    });
  }

  private setDefaultAssignmentSchedule(job: any) {
    const scheduled = job?.scheduled_date ? new Date(job.scheduled_date) : this.nextDefaultScheduleDate();
    this.assignmentDate = this.toDateInput(scheduled);
    this.assignmentTime = `${String(scheduled.getHours()).padStart(2, '0')}:${String(scheduled.getMinutes()).padStart(2, '0')}`;
  }

  private nextDefaultScheduleDate(): Date {
    const date = new Date();
    date.setMinutes(0, 0, 0);
    date.setHours(date.getHours() + 1);
    return date;
  }

  private toDateInput(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  scheduleChangeNeedsReason(job: any, nextScheduledDate: string | null): boolean {
    const current = job?.scheduled_date || null;
    if (!current) return false;
    return this.scheduleKey(current) !== this.scheduleKey(nextScheduledDate);
  }

  scheduleReasonInvalid(job: any, nextScheduledDate: string | null): boolean {
    return this.scheduleChangeNeedsReason(job, nextScheduledDate) && (!this.scheduleChangeCategory || this.scheduleChangeRemarks.trim().length < 5);
  }

  private scheduleKey(value: string | null): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  private resetScheduleChangeReason() {
    this.scheduleChangeCategory = this.scheduleChangeCategories[0];
    this.scheduleChangeRemarks = '';
  }

  private saveOptionalFleetAssignments(job: any, onComplete: () => void) {
    const requests = this.selectedWorkerIds()
      .map(workerId => ({
        workerId,
        vehicleId: Number(this.selectedFleetVehicleIds[workerId] || 0)
      }))
      .filter(item => item.vehicleId > 0)
      .map(item => this.api.assignFleetVehicle({
        id: item.vehicleId,
        assigned_user_id: item.workerId,
        name: this.getWorkerName(item.workerId),
        status: 'Active on Job',
        job: job.id,
        location: job.address || 'Field',
        notes: `Assigned during job dispatch ${job.id}`
      }));

    if (requests.length === 0) {
      onComplete();
      return;
    }

    forkJoin(requests).subscribe({
      next: () => onComplete(),
      error: (err: any) => {
        this.isAllocating.set(false);
        showToast('error', err?.error?.message || 'Job assigned, but fleet assignment failed.');
      }
    });
  }

  openCreateModal(updateUrl = true) {
    this.createMode.set('Job');
    this.newJobCategory.set('Service');
    this.linkedInspectionId = '';
    this.pricingType.set('Hourly');
    this.fixedLabourCharge = null;
    this.emergencyDoubleRate = false;
    this.resetCustomerForm();
    this.showCreateModal.set(true);
    setTimeout(() => this.initAddressAutocomplete(), 0);
    if (updateUrl) {
      this.router.navigate([], { relativeTo: this.route, queryParams: { create: 'job' }, queryParamsHandling: 'merge' });
    }
  }

  openProjectModal(updateUrl = true) {
    this.createMode.set('Project');
    this.linkedInspectionId = '';
    this.resetCustomerForm();
    this.showCreateModal.set(true);
    setTimeout(() => this.initAddressAutocomplete(), 0);
    if (updateUrl) {
      this.router.navigate([], { relativeTo: this.route, queryParams: { create: 'project' }, queryParamsHandling: 'merge' });
    }
  }

  closeCreateModal() {
    this.showCreateModal.set(false);
    this.router.navigate([], { relativeTo: this.route, queryParams: { create: null }, queryParamsHandling: 'merge' });
  }

  resetCustomerForm() {
    this.selectedCustomerId = null;
    this.searchPhone = '';
    this.customerName = '';
    this.customerEmail = '';
    this.customerAddress = '';
    this.addressPlaceId = '';
    this.addressLatitude = null;
    this.addressLongitude = null;
    this.searchResults.set([]);
  }

  onPhoneChange(value: string) {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.selectedCustomerId = null;
    const digits = value.replace(/\D+/g, '');
    if (digits.length >= 5) {
      this.searchTimeout = setTimeout(() => {
        let query = digits.startsWith('0') ? digits.substring(1) : digits;
        
        this.api.searchCustomerByPhone('+64' + query).subscribe({
          next: (res: any) => {
            if (res.success && res.data?.customers) {
              this.searchResults.set(res.data.customers);
            }
          },
          error: () => this.searchResults.set([])
        });
      }, 500);
    } else {
      this.searchResults.set([]);
    }
  }

  selectCustomer(customer: any) {
    this.selectedCustomerId = customer.id ? Number(customer.id) : null;
    this.customerName = customer.name;
    this.customerEmail = customer.email || '';
    this.customerAddress = customer.address || this.customerAddress;
    this.addressPlaceId = customer.address_place_id || '';
    this.addressLatitude = customer.latitude !== undefined && customer.latitude !== null ? Number(customer.latitude) : null;
    this.addressLongitude = customer.longitude !== undefined && customer.longitude !== null ? Number(customer.longitude) : null;
    this.searchPhone = customer.phone.replace(/^\+64\s*/, '').trim();
    this.searchResults.set([]);
  }

  onAddressManualChange() {
    this.addressPlaceId = '';
    this.addressLatitude = null;
    this.addressLongitude = null;
  }

  onSubmit(form: NgForm) {
      if (!this.customerName || !this.customerAddress) {
          showToast('error', 'Customer Name and Address are required.');
          return;
      }
      if (this.createMode() === 'Project' && !this.customerEmail.trim()) {
          showToast('error', 'Customer email is required for creating a project.');
          return;
      }
      if (this.createMode() === 'Job' && this.pricingType() === 'Fixed' && (this.fixedLabourCharge === null || Number(this.fixedLabourCharge) < 0)) {
          showToast('error', 'Enter a valid fixed labour charge.');
          return;
      }
      const contacts = [{ name: this.customerName, phone: '+64 ' + this.searchPhone.trim(), email: this.customerEmail, type: 'Customer' }];

      if (this.createMode() === 'Job') {
        const payload = {
          description: form.value.desc,
          type: this.newJobCategory(),
          priority: this.newJobCategory() === 'Emergency' ? 'Urgent' : 'Routine',
          address: this.customerAddress,
          address_place_id: this.addressPlaceId,
          latitude: this.addressLatitude,
          longitude: this.addressLongitude,
          customer_id: this.selectedCustomerId,
          customer_name: this.customerName,
          contacts: contacts,
          pricing_type: this.pricingType(),
          fixed_labour_charge: this.pricingType() === 'Fixed' ? Number(this.fixedLabourCharge || 0) : null,
          emergency_double_rate: this.newJobCategory() === 'Emergency' && this.emergencyDoubleRate ? 1 : 0,
          linked_inspection_id: this.newJobCategory() === 'Service' && this.linkedInspectionId ? this.linkedInspectionId : null
        };
        this.api.createJob(payload).subscribe({ 
            next: (res: any) => { 
                if (res.success) {
                    showToast('success', 'Job created successfully.');
                    this.loadJobs(); 
                    this.loadInspectionJobs();
                    this.closeCreateModal(); 
                } else {
                    showToast('error', res.message || 'Failed to create job.');
                }
            },
            error: () => showToast('error', 'Network or server error occurred.')
        });
      } else {
        const payload = {
          title: `${this.customerName} Project`,
          description: form.value.desc,
          address: this.customerAddress,
          address_place_id: this.addressPlaceId,
          latitude: this.addressLatitude,
          longitude: this.addressLongitude,
          customer_id: this.selectedCustomerId,
          customer_name: this.customerName,
          contacts: contacts
        };
        this.api.createProject(payload).subscribe({ 
            next: (res: any) => { 
                if (res.success) {
                    showToast('success', 'Development Project created successfully.');
                    this.loadJobs(); 
                    this.closeCreateModal(); 
                } else {
                    showToast('error', res.message || 'Failed to create project.');
                }
            },
            error: () => showToast('error', 'Network or server error occurred.')
        });
      }
  }

  private loadMapSettings() {
    this.api.getCompanySettings().subscribe({
      next: (res: any) => {
        const key = String(res?.data?.google_maps_browser_api_key || '').trim();
        this.mapsKey.set(key);
        if (key) this.loadGoogleMapsScript(key);
      }
    });
  }

  private loadGoogleMapsScript(key: string) {
    if (typeof window === 'undefined') return;
    if ((window as any).google?.maps?.places) {
      this.mapsLoaded.set(true);
      this.initAddressAutocomplete();
      return;
    }
    const existing = document.getElementById('google-maps-js');
    if (existing) {
      existing.addEventListener('load', () => {
        this.mapsLoaded.set(true);
        this.initAddressAutocomplete();
      });
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-js';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.mapsLoaded.set(true);
      this.initAddressAutocomplete();
    };
    document.head.appendChild(script);
  }

  private initAddressAutocomplete() {
    if (!this.siteAddressInput?.nativeElement || !(window as any).google?.maps?.places) return;
    const google = (window as any).google;
    const autocomplete = new google.maps.places.Autocomplete(this.siteAddressInput.nativeElement, {
      componentRestrictions: { country: 'nz' },
      bounds: this.aucklandBounds(),
      strictBounds: true,
      fields: ['formatted_address', 'geometry', 'place_id', 'address_components']
    });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const lat = place.geometry?.location ? Number(place.geometry.location.lat()) : null;
      const lng = place.geometry?.location ? Number(place.geometry.location.lng()) : null;
      if (!this.isInAucklandBounds(lat, lng)) {
        this.addressPlaceId = '';
        this.addressLatitude = null;
        this.addressLongitude = null;
        showToast('error', 'Please select an Auckland address.');
        return;
      }
      this.customerAddress = place.formatted_address || this.customerAddress;
      this.addressPlaceId = place.place_id || '';
      this.addressLatitude = lat;
      this.addressLongitude = lng;
    });
  }

  private aucklandBounds() {
    return {
      north: -36.35,
      south: -37.35,
      east: 175.45,
      west: 174.15
    };
  }

  private isInAucklandBounds(lat: number | null, lng: number | null): boolean {
    if (lat === null || lng === null) return false;
    const bounds = this.aucklandBounds();
    return lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east;
  }
}




